import { X } from "lucide-react";
import { matchHorse } from "../lib/horseSearch";
import { SmartSearchPicker } from "./SmartSearchPicker";

const hmatch = (h, q) => matchHorse({ horse_name: h.display_name, horse_official_name: h.official_name, horse_microchip: h.microchip, horse_ueln: h.ueln }, q);
const optExtra = (h) => (
  <>
    {(h.official_name && h.official_name !== h.display_name) ? <span className="text-[11px] text-[#8C6D4F]"> · {h.official_name}</span> : ""}
    {h.microchip ? <span className="text-[10px] text-[#8C6D4F]"> · {h.microchip}</span> : ""}
  </>
);

// Shared "Cerca cavallo…" autocomplete (name / official name / microchip / UELN via matchHorse).
// Single mode: value = horse id, onChange(id, horse). Multiple mode: value = [ids], onChange([ids]).
// Mobile-safe via SmartSearchPicker: inline popover on desktop, bottom sheet on phones.
export function HorsePicker({ horses = [], value, onChange, testId, multiple = false,
                             placeholder = "Cerca cavallo (nome, microchip, UELN)…", disabled }) {
  if (multiple) {
    const ids = value || [];
    const chosen = ids.map((id) => horses.find((h) => h.id === id)).filter(Boolean);
    return (
      <div>
        <div className="flex flex-wrap gap-1.5 mb-1.5">
          {chosen.map((h) => (
            <span key={h.id} data-testid={`${testId}-chip-${h.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#1B3B2B] text-white text-xs px-2.5 py-1">
              {h.display_name}
              <button type="button" data-testid={`${testId}-chip-remove-${h.id}`} onMouseDown={(e) => { e.preventDefault(); onChange(ids.filter((x) => x !== h.id)); }} className="text-white/80 hover:text-white"><X className="h-3 w-3" /></button>
            </span>
          ))}
          {chosen.length === 0 && <span className="text-xs text-[#8C7A5E]">Nessun cavallo selezionato</span>}
        </div>
        <SmartSearchPicker testId={testId} placeholder={placeholder} disabled={disabled} mobileTitle="Aggiungi cavalli">
          {(q, { clearQuery, optionProps }) => {
            const filtered = horses.filter((h) => !ids.includes(h.id)).filter((h) => !q.trim() || hmatch(h, q));
            if (filtered.length === 0) return <p className="px-3 py-2 text-sm text-[#8C6D4F]">Nessun cavallo</p>;
            return filtered.slice(0, 40).map((h) => (
              <button key={h.id} type="button" data-testid={`${testId}-opt-${h.id}`}
                {...optionProps(() => onChange([...ids, h.id]))}
                className="w-full text-left px-3 py-2 text-sm hover:bg-[#F0EAE1] text-[#1B3B2B]">
                {h.display_name}{optExtra(h)}
              </button>
            ));
          }}
        </SmartSearchPicker>
        <p className="text-[11px] text-[#8C7A5E] mt-1" data-testid={`${testId}-count`}>{ids.length} cavall{ids.length === 1 ? "o" : "i"} selezionat{ids.length === 1 ? "o" : "i"}</p>
      </div>
    );
  }

  const sel = horses.find((h) => h.id === value);
  return (
    <SmartSearchPicker testId={testId} placeholder={placeholder} disabled={disabled}
      displayLabel={sel ? sel.display_name : ""} mobileTitle="Scegli cavallo"
      onClear={value ? () => onChange("", null) : undefined}>
      {(q, { close, optionProps }) => {
        const filtered = q.trim() ? horses.filter((h) => hmatch(h, q)) : horses;
        if (filtered.length === 0) return <p className="px-3 py-2 text-sm text-[#8C6D4F]">Nessun cavallo</p>;
        return filtered.slice(0, 40).map((h) => (
          <button key={h.id} type="button" data-testid={`${testId}-opt-${h.id}`}
            {...optionProps(() => { onChange(h.id, h); close(); })}
            className="w-full text-left px-3 py-2 text-sm hover:bg-[#F0EAE1] text-[#1B3B2B]">
            {h.display_name}{optExtra(h)}
          </button>
        ));
      }}
    </SmartSearchPicker>
  );
}
